import React, { useState } from "react";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

import UserAvatar from "../../management/UserAvatar";
import ConversationGroupAvatar from "../ConversationGroupAvatar";
import ConversationDetailMobile from "./ConversationDetailMobile";
import ToolbarWithLogo from "../../ToolbarWithLogo";

function getLastMessage(conversation) {
    const messages = conversation.messages || [];
    return messages.length > 0 ? messages[messages.length - 1].message : "";
}

function ConversationListLoading() {
    return (
        <Box role="status" aria-label="Loading conversations">
            {[0, 1, 2].map((index) => (
                <Box
                    key={index}
                    sx={{
                        minHeight: 72,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.75,
                        py: 1.5,
                    }}
                >
                    <Skeleton
                        variant="circular"
                        width={40}
                        height={40}
                        sx={{ flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="55%" height={22} />
                        <Skeleton variant="text" width="76%" height={18} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

export default function ConversationListMobile(props) {
    const [detailDialogIsOpen, setDetailDialogIsOpen] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState();
    const activeConversations = props.conversations.filter(
        (conversation) => conversation.deleted === false,
    );

    const handleConversationClick = (conversation) => () => {
        setDetailDialogIsOpen(true);
        setSelectedConversation(conversation);
    };

    function handleCloseDetailDialog() {
        setDetailDialogIsOpen(false);
    }

    return (
        <>
            <Box sx={{ px: 1.5, pt: 2, pb: 2.5 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                        mb: 2,
                        px: 0.5,
                    }}
                >
                    <Box>
                        <Typography variant="h4" component="h1">
                            Messages
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.35 }}
                        >
                            {props.loading
                                ? "Loading conversations…"
                                : "Keep in touch with people in your hubs."}
                        </Typography>
                    </Box>
                    {props.canAdd && (
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={props.handleAddClick}
                            startIcon={<AddIcon />}
                            sx={{
                                mt: 0.25,
                                flexShrink: 0,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 700,
                            }}
                        >
                            New
                        </Button>
                    )}
                </Box>

                <Paper
                    variant="outlined"
                    sx={{
                        overflow: "hidden",
                        borderRadius: 3,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        boxShadow: "0 8px 28px rgba(20, 35, 25, 0.07)",
                    }}
                >
                    <List disablePadding>
                        {props.loading && <ConversationListLoading />}

                        {!props.loading && activeConversations.map((conversation, index) => {
                            const preview = getLastMessage(conversation);
                            const avatar =
                                conversation.otherUsers.length === 1 ? (
                                    <UserAvatar
                                        userOrHub={conversation.otherUsers[0]}
                                        path="users"
                                        variant="circular"
                                    />
                                ) : (
                                    <ConversationGroupAvatar
                                        conversation={conversation}
                                    />
                                );

                            return (
                                <ListItemButton
                                    key={conversation.key}
                                    onClick={handleConversationClick(
                                        conversation,
                                    )}
                                    sx={{
                                        gap: 1.5,
                                        minHeight: 72,
                                        px: 1.75,
                                        py: 1.5,
                                        borderBottom:
                                            index < activeConversations.length - 1
                                                ? "1px solid"
                                                : "none",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Badge
                                        badgeContent={
                                            conversation.unreadMessagesCount
                                        }
                                        color="secondary"
                                        overlap="circular"
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            flexShrink: 0,
                                            "& .MuiAvatar-root": {
                                                width: 40,
                                                height: 40,
                                            },
                                            "& .MuiBadge-badge": {
                                                minWidth: 19,
                                                height: 19,
                                                px: 0.5,
                                                fontSize: "0.68rem",
                                                fontWeight: 800,
                                                border: "2px solid",
                                                borderColor: "background.paper",
                                            },
                                        }}
                                    >
                                        {avatar}
                                    </Badge>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle2"
                                            noWrap
                                            sx={{
                                                fontWeight:
                                                    conversation.unreadMessagesCount >
                                                    0
                                                        ? 800
                                                        : 700,
                                            }}
                                        >
                                            {conversation.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                            sx={{ mt: 0.2 }}
                                        >
                                            {preview || "No messages yet"}
                                        </Typography>
                                    </Box>
                                    {conversation.unreadMessagesCount > 0 && (
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                flexShrink: 0,
                                                borderRadius: "50%",
                                                backgroundColor: "primary.main",
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            );
                        })}

                        {!props.loading && activeConversations.length === 0 && (
                            <Box
                                sx={{
                                    minHeight: 310,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 4,
                                    textAlign: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        display: "grid",
                                        placeItems: "center",
                                        mb: 1.5,
                                        borderRadius: 2.5,
                                        color: "primary.main",
                                        backgroundColor:
                                            "rgba(0, 149, 61, 0.08)",
                                    }}
                                >
                                    <ForumOutlinedIcon />
                                </Box>
                                <Typography variant="h6">
                                    No conversations yet
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    Start a conversation to connect with someone
                                    in your hubs.
                                </Typography>
                            </Box>
                        )}
                    </List>
                </Paper>
            </Box>
            <ConversationDetailMobile
                open={detailDialogIsOpen}
                onClose={handleCloseDetailDialog}
                conversation={selectedConversation}
            />
            <ToolbarWithLogo />
        </>
    );
}
